import isEmpty from 'lodash/isEmpty';
import type { DocNode as ADF } from '@atlaskit/adf-schema';
import type {
	FormFieldValues,
	GeneralFieldValue,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type {
	IssueLink,
	IssueLinkServerRequest,
} from '@atlassian/jira-issue-field-issue-links/src/common/types.tsx';
import {
	ATTACHMENT_TYPE,
	ISSUE_LINKS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { isOnlyWhitespaceAdf } from '@atlassian/jira-rich-content/src/common/adf-parsing-utils.tsx';
import { convertAdfToText } from '@atlassian/jira-rich-content/src/common/convert-adf-to-text/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	UPDATE_FIELDS,
	UPDATE_FIELDS_CREATE_LINKED_ISSUE,
	getGqlFieldMap,
} from '../../common/constants/field-map.tsx';
import type {
	Context,
	InteractedFields,
	KeyedFieldsFieldConfig,
} from '../../common/types/index.tsx';
import { NUMBER_FIELD } from '../../common/constants/graphql-field-types.tsx';
import type {
	IssueCreateFieldsUsed,
	IssueCreateContextUsed,
	AttachmentFieldSubmitValue,
} from './types.tsx';

export const transformData = (
	formData: FormFieldValues,
	fieldConfig: KeyedFieldsFieldConfig,
): FormFieldValues =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Object.keys(formData).reduce<Record<string, any>>((acc, fieldId) => {
		// context fields are not part of field config
		const fieldData = fieldConfig[fieldId];
		if (fieldData) {
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly JiraAffectedServicesField: { readonly Component: AbstractComponent<ReturnComponentProps<AffectedServicesFieldConfig>, any>; readonly defaultValueKey: "selectedAffectedServices"; readonly submitTransform: (options: AffectedServiceOption[]) => ServiceEntity[]; readonly retainUserInputTransform: (selectedAff...'.
			const transformFunction = getGqlFieldMap()[fieldData.gqlType]?.submitTransform;
			const transformedData =
				transformFunction && formData[fieldId] !== null && formData[fieldId] !== undefined
					? transformFunction(
							formData[fieldId],
							fieldData.gqlType === NUMBER_FIELD &&
								fg('frontier-currency-field-fix-percentage-storage')
								? fieldData
								: undefined,
						)
					: formData[fieldId];
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			return { ...acc, [fieldId]: transformedData };
		}
		// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
		return { ...acc, [fieldId]: formData[fieldId] };
	}, {});

export const isFieldPopulated = (fieldValue: GeneralFieldValue) => {
	if (typeof fieldValue === 'object' && fieldValue !== null) {
		// check for linked issue field
		if ('issues' in fieldValue) {
			return fieldValue.issues.length > 0;
		}
		// check for rich text field
		if ('content' in fieldValue && 'version' in fieldValue && 'type' in fieldValue) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return !isOnlyWhitespaceAdf(fieldValue as ADF);
		}
	}

	return typeof fieldValue === 'number' || typeof fieldValue === 'boolean' || !isEmpty(fieldValue);
};

export const transformDataToIssueCreateFieldsUsed = (
	formData: FormFieldValues,
	fieldConfig: KeyedFieldsFieldConfig,
	interactedFields: InteractedFields,
	collapsedFields: string[],
): IssueCreateFieldsUsed => {
	const issueCreateFieldsUsed: Record<string, string | boolean | number | undefined>[] = [];
	Object.keys(fieldConfig).forEach((fieldId) => {
		const fieldValue = formData[fieldId];
		const configForField = fieldConfig[fieldId];

		const fieldEntry: Record<string, string | boolean | number | undefined> = {
			issueFieldId: fieldId,
			issueFieldType: fieldConfig[fieldId].fieldType,
			gqlType: fieldConfig[fieldId].gqlType,
			isPopulated: isFieldPopulated(fieldValue),
			isRequired: !!fieldConfig[fieldId].isRequired,
			hasDefaultValue: 'defaultValue' in configForField && !!configForField.defaultValue,
			wasInteractedWith: !!interactedFields?.[fieldId],
			wasCollapsed: collapsedFields.includes(fieldId),
		};

		// Add description length for description field
		if (
			fieldId === 'description' &&
			fieldValue &&
			typeof fieldValue === 'object' &&
			fg('issue_create_send_description_character_length')
		) {
			const descriptionAdf = fieldValue satisfies ADF;
			fieldEntry.descriptionLength = (convertAdfToText(descriptionAdf) || '').length;
		}

		issueCreateFieldsUsed.push(fieldEntry);
	});

	// @ts-expect-error - TS7005 - Variable 'issueCreateFieldsUsed' implicitly has an 'any[]' type.
	return issueCreateFieldsUsed;
};

export const transformContextDataUsedForCreate = (context?: Context): IssueCreateContextUsed => {
	if (isEmpty(context)) return {};

	return {
		wereAdditionalFieldsProvided: !isEmpty(context?.submitApiData?.additionalFields),
		isIssueListProvided: !isEmpty(context?.submitApiData?.issueList?.addTo),
	};
};

export const getAttachmentsSubmitValue = ({
	attachments,
	issueLinksValue,
	sourceIssueKey,
}: {
	attachments: string[];
	issueLinksValue: IssueLink;
	sourceIssueKey: string;
}): AttachmentFieldSubmitValue[] => [
	...(attachments
		? [
				{
					set: attachments,
				},
			]
		: []),
	...(issueLinksValue?.includeAttachments
		? [
				{
					copy: {
						sourceIssues: [{ key: sourceIssueKey }],
					},
				},
			]
		: []),
];

export const getIssueLinksSubmitValue = ({
	issueLinksTransformedData,
	issueLinksValue,
	sourceIssueKey,
}: {
	issueLinksTransformedData: IssueLinkServerRequest[] | undefined;
	issueLinksValue: IssueLink;
	sourceIssueKey: string;
}) => [
	...(issueLinksTransformedData || []),
	...(issueLinksValue?.includeLinks
		? [
				{
					copy: {
						sourceIssues: [{ key: sourceIssueKey }],
					},
				},
			]
		: []),
];

export const handleCopyLinksAndAttachments = ({
	transformedData,
	formData,
	sourceIssueKey,
}: {
	transformedData: FormFieldValues;
	formData: FormFieldValues;
	sourceIssueKey: string;
}) => {
	const issueLinksValue: IssueLink = formData[ISSUE_LINKS_TYPE];
	return {
		...transformedData,
		[ATTACHMENT_TYPE]: getAttachmentsSubmitValue({
			attachments: transformedData[ATTACHMENT_TYPE],
			issueLinksValue,
			sourceIssueKey,
		}),
		[ISSUE_LINKS_TYPE]: getIssueLinksSubmitValue({
			issueLinksTransformedData: transformedData[ISSUE_LINKS_TYPE],
			issueLinksValue,
			sourceIssueKey,
		}),
	};
};

export const getUpdateFields = (isCreateLinkedIssue = false) =>
	isCreateLinkedIssue ? UPDATE_FIELDS_CREATE_LINKED_ISSUE : UPDATE_FIELDS;
